import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { BoosterDefinitions } from './boosters/types';
import { BoosterType } from './boosters/types';
import type { AppStateController } from '../app/state/AppStateController';
import { BoosterButtonView } from './boosters/ui/BoosterButtonView';
import type { BoardModel } from './boardMVC/models/board/BoardModel';
import type { TrayModel } from './boardMVC/models/tray/TrayModel';
import { TileColor } from './boardMVC/TileColor';

export class BoosterPanelView extends Container {
  public readonly onBoosterClicked = new Signal<(boosterType: BoosterType) => void>();

  private readonly buttonSpacing = 165;

  private readonly buttonContainer: Container = new Container().props({
    parent: this,
  });

  private readonly buttonViews: BoosterButtonView[] = [];

  constructor(
    private readonly state: AppStateController,
    private readonly economyMap: BoosterDefinitions,
    private readonly canBuyBooster: (booster: BoosterType) => boolean,
    private readonly canExecuteBooster: (booster: BoosterType) => boolean,
    private readonly canShowShop: () => boolean,
  ) {
    super();

    this.createBoosterButtons();
    this.updateButtonStates();
    this.addStateSubscriptions();
  }

  public getButton(boosterType: BoosterType): BoosterButtonView | undefined {
    return this.buttonViews.find((button) => button.boosterType === boosterType);
  }

  private createBoosterButtons(): BoosterButtonView[] {
    return [BoosterType.Hammer, BoosterType.Redraw, BoosterType.Move, BoosterType.Undo]
      .sort((a, b) => this.economyMap[a].unlockLevel - this.economyMap[b].unlockLevel)
      .map((type) => this.createBoosterButton(type));
  }

  private createBoosterButton(boosterType: BoosterType): BoosterButtonView {
    const { state, economyMap } = this;
    const button = new BoosterButtonView(
      state,
      boosterType,
      economyMap[boosterType],
      () => {
        this.onBoosterClicked.emit(boosterType);
      },
      this.canShowShop,
    );
    this.addBoosterButton(button);
    return button;
  }

  private addBoosterButton(button: BoosterButtonView): void {
    const buttonX = this.buttonViews.length * this.buttonSpacing + button.width / 2;
    this.buttonContainer.addChild(button);
    this.buttonViews.push(button);
    button.x = buttonX;
    this.buttonContainer.x = -(this.buttonSpacing * this.buttonViews.length) / 2;
  }

  public setHorizontalLayout(value: boolean): void {
    if (value) {
      this.setHorizontal();
    } else {
      this.setPortrait();
    }
  }

  private setHorizontal(): void {
    this.buttonViews.forEach((button, index) => {
      button.props({
        x: 0,
        y: index * this.buttonSpacing + button.height * 0.5,
      });
    });
    this.buttonContainer.props({
      x: 0,
      y: this.buttonSpacing * this.buttonViews.length * -0.5,
    });
  }

  private setPortrait(): void {
    this.buttonViews.forEach((button, index) => {
      button.props({
        x: index * this.buttonSpacing + button.height * 0.5,
        y: 0,
      });
    });
    this.buttonContainer.props({
      x: this.buttonSpacing * this.buttonViews.length * -0.5,
      y: 0,
    });
  }

  public updateButtonStates(): void {
    this.buttonViews.forEach((button: BoosterButtonView) => {
      button.enabled = this.canExecuteBooster(button.boosterType) || this.canBuyBooster(button.boosterType);
    });
  }

  public hookupBoard(boardModel: BoardModel, trayModel: TrayModel): void {
    const checkForNearFailure = (): void => {
      const redrawBoosterButton = this.getButton(BoosterType.Redraw);
      if (!redrawBoosterButton?.enabled) return;

      const trayTopColors = trayModel.getTopColors().filter((color) => color !== TileColor.None);
      if (trayTopColors.length === 0) return;

      redrawBoosterButton.attractMode =
        boardModel.getEmptyStackCount() <= 3 &&
        !boardModel.getEmptyStacks().some((stack) => stack.isConnectedToColors(trayTopColors), true);
    };

    boardModel.onStateUpdate.connect(() => {
      this.updateButtonStates();
      checkForNearFailure();
    });
    trayModel.onRefilled.connect(checkForNearFailure);
  }

  private addStateSubscriptions(): void {
    const { state } = this;
    state.subscriptions.onChange(
      ['boosters', 'coins'],
      () => {
        this.updateButtonStates();
      },
      this,
    );

    state.subscriptions.onChange(
      ['horizontalLayout'],
      () => {
        this.setHorizontalLayout(state.isHorizontalLayoutActive());
      },
      this,
    );
    this.setHorizontalLayout(state.isHorizontalLayoutActive());

    const undoButton = this.getButton(BoosterType.Undo);
    const checkUndo = (): void => {
      if (!undoButton) return;
      undoButton.enabled = this.canExecuteBooster(BoosterType.Undo) || this.canBuyBooster(BoosterType.Undo);
    };

    this.pixi.ticker.add(checkUndo);

    this.on('destroyed', () => {
      state.subscriptions.removeAllSubscriptions(this);
      this.pixi.ticker.remove(checkUndo);
    });
  }
}
